import React from 'react'

import { withToggles, type WithTogglesProps } from '@acorns/web-toggles-react'

import { APP_ID } from 'app-constants'
import trackEvent from 'utils/analytics/trackEvent'
import { ReactErrorBoundaryError, sendToSentry } from 'utils/errors'

import { ErrorState } from './ErrorState'
import { datadogRum } from '@datadog/browser-rum'

type Props = {
  children: React.ReactNode
} & WithTogglesProps

class ErrorBoundaryClass extends React.Component<Props, { hasError: boolean }> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: any) {
    const userId = this.context.userId

    datadogRum.addError(error, { event: 'Page Crash' })

    trackEvent('Page Crash', {
      eventData: {
        app: APP_ID,
        error: error.toString(),
        enabled_feature_toggles: this.props.toggles.getAllEnabledFeatures().toString(),
        error_boundary_url_location: window.location.href,
        ...info,
      },
    })

    sendToSentry(new ReactErrorBoundaryError(error, window.location.href), {
      extras: {
        userId,
        enabled_feature_toggles: this.props.toggles.getAllEnabledFeatures(),
        info,
        localStorage: { ...localStorage },
        sessionStorage: { ...sessionStorage },
      },
    })

    this.setState({ hasError: true })
  }

  render() {
    if (this.state.hasError) {
      return <ErrorState />
    }

    return this.props.children
  }
}

export const ErrorBoundary = withToggles(ErrorBoundaryClass)
